import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, KeyRound } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["manager", "cashier", "waiter", "kitchen", "driver"];

export default function StaffManagement() {
    const { t, lang } = useApp();
    const [staff, setStaff] = useState([]);
    const [dlg, setDlg] = useState(false);
    const [form, setForm] = useState({ name: "", role: "waiter", pin: "" });
    const [pinDlg, setPinDlg] = useState(null); // staff id
    const [newPin, setNewPin] = useState("");

    const load = async () => {
        const r = await api.get("/staff");
        setStaff(r.data);
    };

    useEffect(() => { load(); }, []);

    const add = async () => {
        if (!/^\d{4}$/.test(form.pin)) { toast.error(t("error") + ": PIN 4 digits"); return; }
        if (!form.name.trim()) { toast.error(t("error")); return; }
        try {
            await api.post("/staff", form);
            toast.success(t("added"));
            setDlg(false);
            setForm({ name: "", role: "waiter", pin: "" });
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || t("error"));
        }
    };

    const remove = async (id) => {
        if (!window.confirm(t("confirm_delete"))) return;
        await api.delete(`/staff/${id}`);
        await load();
    };

    const toggleActive = async (s) => {
        await api.patch(`/staff/${s.id}`, { active: !s.active });
        await load();
    };

    const resetPin = async () => {
        if (!/^\d{4}$/.test(newPin)) { toast.error("PIN 4 digits"); return; }
        try {
            await api.patch(`/staff/${pinDlg}`, { pin: newPin });
            toast.success(t("save"));
            setPinDlg(null);
            setNewPin("");
        } catch (e) {
            toast.error(e.response?.data?.detail || t("error"));
        }
    };

    return (
        <div className="space-y-4" data-testid="staff-mgmt-page">
            <div className="flex items-center justify-between">
                <h1 className={`${lang === "ar" ? "font-ar" : "font-display-en italic"} text-3xl`}>{t("manage_staff")}</h1>
                <Button onClick={() => setDlg(true)} data-testid="add-staff-btn"><Plus className="h-4 w-4 me-2" />{t("add_staff")}</Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {staff.length === 0 ? (
                    <div className="col-span-full text-center py-10 text-muted-foreground">—</div>
                ) : staff.map((s) => (
                    <Card key={s.id} className="grain bg-card/70" data-testid={`staff-row-${s.id}`}>
                        <CardContent className="p-5">
                            <div className="flex items-start justify-between">
                                <div>
                                    <div className="font-medium text-lg">{s.name}</div>
                                    <div className="text-xs text-muted-foreground capitalize mt-1">{t(s.role)}</div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Switch checked={s.active} onCheckedChange={() => toggleActive(s)} data-testid={`staff-active-${s.id}`} />
                                </div>
                            </div>
                            <div className="flex gap-2 mt-4">
                                <Button size="sm" variant="outline" className="flex-1" onClick={() => setPinDlg(s.id)} data-testid={`reset-pin-${s.id}`}>
                                    <KeyRound className="h-3.5 w-3.5 me-2" />{t("new_pin")}
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => remove(s.id)} data-testid={`delete-staff-${s.id}`}>
                                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            <Dialog open={dlg} onOpenChange={setDlg}>
                <DialogContent data-testid="staff-dialog">
                    <DialogHeader><DialogTitle>{t("add_staff")}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div className="space-y-1.5"><Label>{t("name")}</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-staff-name" /></div>
                        <div className="space-y-1.5">
                            <Label>{t("role")}</Label>
                            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                                <SelectTrigger data-testid="new-staff-role"><SelectValue /></SelectTrigger>
                                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{t(r)}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5"><Label>{t("pin")}</Label><Input type="text" inputMode="numeric" pattern="\d{4}" maxLength={4} value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "").slice(0, 4) })} data-testid="new-staff-pin" /></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDlg(false)}>{t("cancel")}</Button>
                        <Button onClick={add} data-testid="save-staff-btn">{t("save")}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={!!pinDlg} onOpenChange={(o) => !o && setPinDlg(null)}>
                <DialogContent data-testid="pin-reset-dialog">
                    <DialogHeader><DialogTitle>{t("new_pin")}</DialogTitle></DialogHeader>
                    <Input
                        type="text" inputMode="numeric" pattern="\d{4}" maxLength={4}
                        value={newPin}
                        onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                        data-testid="new-pin-input"
                        autoFocus
                    />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setPinDlg(null); setNewPin(""); }}>{t("cancel")}</Button>
                        <Button onClick={resetPin} data-testid="save-pin-btn">{t("save")}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
